import { Injectable } from '@angular/core';
import { HttpHeaders } from '@angular/common/http';
import { EMPTY, Observable, concat, of } from 'rxjs';
import { catchError, finalize, shareReplay, tap } from 'rxjs/operators';

import { HttpService } from '~/core/services/http.service';
import {
    HOME_API_DEFAULT_PAGE,
    HOME_API_DEFAULT_PAGE_SIZE,
    HOME_API_PATHS
} from './home-api.constants';

const DELETE_ASSET_TIMEOUT_MS = 60000;
const ASSET_DETAIL_TIMEOUT_MS = 60000;
const ASSET_MENTIONS_TIMEOUT_MS = 60000;

interface CacheEntry<T> {
    value: T;
    cachedAt: number;
}

@Injectable({
    providedIn: 'root'
})
export class HomeApiService {
    // Caches the in-flight/last response so re-navigating between '/' and '/run/:runId'
    // (which recreates HomeComponent) doesn't re-fetch the same run list from the API.
    private runsCache$: Observable<unknown> | null = null;
    private refineriesCache$: Observable<unknown> | null = null;
    private readonly runSummaryCache = new Map<string, CacheEntry<unknown>>();
    private readonly runAssetsCache = new Map<string, CacheEntry<unknown>>();
    private readonly processSummaryCache = new Map<string, CacheEntry<unknown>>();
    private readonly citationsManifestCache = new Map<string, CacheEntry<unknown>>();

    private readonly runSummaryInFlight = new Map<string, Observable<unknown>>();
    private readonly runAssetsInFlight = new Map<string, Observable<unknown>>();
    private readonly processSummaryInFlight = new Map<string, Observable<unknown>>();
    private readonly citationsManifestInFlight = new Map<string, Observable<unknown>>();

    constructor(private readonly httpService: HttpService) {}

    public getRuns<T>(): Observable<T> {
        if (!this.runsCache$) {
            this.runsCache$ = this.httpService.Get<T>(HOME_API_PATHS.runs).pipe(
                shareReplay({ bufferSize: 1, refCount: false })
            );
        }

        return this.runsCache$ as Observable<T>;
    }

    public getRefineries<T>(): Observable<T> {
        if (!this.refineriesCache$) {
            this.refineriesCache$ = this.httpService.Get<T>(HOME_API_PATHS.refineries).pipe(
                shareReplay({ bufferSize: 1, refCount: false })
            );
        }

        return this.refineriesCache$ as Observable<T>;
    }

    public getRunSummary<T>(runId: string, refinery: string, unit: string): Observable<T> {
        const normalizedRunId = runId.trim();
        const normalizedRefinery = refinery.trim().toUpperCase();
        const normalizedUnit = unit.trim().toUpperCase();
        const key = `${normalizedRefinery.toLowerCase()}|${normalizedUnit.toLowerCase()}|${normalizedRunId}`;

        return this.getWithCacheAndBackgroundRefresh<T>(
            this.runSummaryCache,
            this.runSummaryInFlight,
            key,
            () => this.httpService.Get<T>(
                `${HOME_API_PATHS.refineries}/${encodeURIComponent(normalizedRefinery)}/units/${encodeURIComponent(normalizedUnit)}/runs/${encodeURIComponent(normalizedRunId)}/${HOME_API_PATHS.summary}`
            )
        );
    }

    public getRunAssets<T>(runId: string, refinery: string, unit: string): Observable<T> {
        const normalizedRunId = runId.trim();
        const normalizedRefinery = refinery.trim().toUpperCase();
        const normalizedUnit = unit.trim().toUpperCase();
        const key = `${normalizedRefinery.toLowerCase()}|${normalizedUnit.toLowerCase()}|${normalizedRunId}`;

        return this.getWithCacheAndBackgroundRefresh<T>(
            this.runAssetsCache,
            this.runAssetsInFlight,
            key,
            () => this.httpService.Get<T>(
                `${HOME_API_PATHS.refineries}/${encodeURIComponent(normalizedRefinery)}/units/${encodeURIComponent(normalizedUnit)}/runs/${encodeURIComponent(normalizedRunId)}/${HOME_API_PATHS.assets}`
            )
        );
    }

    public getProcessSummary<T>(runId: string, refinery: string, unit: string): Observable<T> {
        const normalizedRunId = runId.trim();
        const normalizedRefinery = refinery.trim().toUpperCase();
        const normalizedUnit = unit.trim().toUpperCase();
        const key = `${normalizedRefinery.toLowerCase()}|${normalizedUnit.toLowerCase()}|${normalizedRunId}`;

        return this.getWithCacheAndBackgroundRefresh<T>(
            this.processSummaryCache,
            this.processSummaryInFlight,
            key,
            () => this.httpService.Get<T>(
                `${HOME_API_PATHS.refineries}/${encodeURIComponent(normalizedRefinery)}/units/${encodeURIComponent(normalizedUnit)}/runs/${encodeURIComponent(normalizedRunId)}/${HOME_API_PATHS.processSummary}`
            )
        );
    }

    public getCitationsManifest<T>(runId: string, refinery: string, unit: string): Observable<T> {
        const normalizedRunId = runId.trim();
        const normalizedRefinery = refinery.trim().toUpperCase();
        const normalizedUnit = unit.trim().toUpperCase();
        const key = `${normalizedRefinery.toLowerCase()}|${normalizedUnit.toLowerCase()}|${normalizedRunId}`;

        return this.getWithCacheAndBackgroundRefresh<T>(
            this.citationsManifestCache,
            this.citationsManifestInFlight,
            key,
            () => this.httpService.Get<T>(
                `${HOME_API_PATHS.refineries}/${encodeURIComponent(normalizedRefinery)}/units/${encodeURIComponent(normalizedUnit)}/runs/${encodeURIComponent(normalizedRunId)}/${HOME_API_PATHS.citationsManifest}`
            )
        );
    }

    public getAssetDetail<T>(runId: string, refinery: string, unit: string, assetTag: string): Observable<T> {
        const normalizedRunId = runId.trim();
        const normalizedRefinery = refinery.trim().toUpperCase();
        const normalizedUnit = unit.trim().toUpperCase();
        const normalizedAssetTag = assetTag.trim();

        return this.httpService.Get<T>(
            `${HOME_API_PATHS.refineries}/${encodeURIComponent(normalizedRefinery)}/units/${encodeURIComponent(normalizedUnit)}/runs/${encodeURIComponent(normalizedRunId)}/${HOME_API_PATHS.assets}/${encodeURIComponent(normalizedAssetTag)}`,
            undefined,
            ASSET_DETAIL_TIMEOUT_MS
        );
    }

    public updateAssetApprovals<T>(runId: string, refinery: string, unit: string, assetTag: string, payload: unknown): Observable<T> {
        const normalizedRunId = runId.trim();
        const normalizedRefinery = refinery.trim().toUpperCase();
        const normalizedUnit = unit.trim().toUpperCase();
        const normalizedAssetTag = assetTag.trim();

        const formData = new FormData();
        formData.append('payload', JSON.stringify(payload));

        // Keep Content-Type unset so browser can add multipart boundary.
        const multipartHeaders = new HttpHeaders({ accept: 'application/json' });

        return this.httpService.Post<T>(
            `${HOME_API_PATHS.refineries}/${encodeURIComponent(normalizedRefinery)}/units/${encodeURIComponent(normalizedUnit)}/runs/${encodeURIComponent(normalizedRunId)}/${HOME_API_PATHS.approvals}/${HOME_API_PATHS.assets}/${encodeURIComponent(normalizedAssetTag)}/${HOME_API_PATHS.edits}`,
            formData,
            multipartHeaders
        ).pipe(
            tap(() => this.invalidateRunScopedCaches(normalizedRunId))
        );
    }

    public deleteAsset<T>(runId: string, refinery: string, unit: string, assetTag: string, comment: string): Observable<T> {
        const normalizedRunId = runId.trim();
        const normalizedRefinery = refinery.trim().toUpperCase();
        const normalizedUnit = unit.trim().toUpperCase();
        const normalizedAssetTag = assetTag.trim();
        const payload = { comment: comment.trim() };

        return this.httpService.Delete<T>(
            `${HOME_API_PATHS.refineries}/${encodeURIComponent(normalizedRefinery)}/units/${encodeURIComponent(normalizedUnit)}/runs/${encodeURIComponent(normalizedRunId)}/${HOME_API_PATHS.assets}/${encodeURIComponent(normalizedAssetTag)}`,
            payload,
            undefined,
            DELETE_ASSET_TIMEOUT_MS
        ).pipe(
            tap(() => this.invalidateRunScopedCaches(normalizedRunId))
        );
    }

    public regeneratePdf<T>(runId: string): Observable<T> {
        return this.httpService.Post<T>(
            `${HOME_API_PATHS.runs}/${encodeURIComponent(runId)}/${HOME_API_PATHS.approvals}/${HOME_API_PATHS.regeneratePdf}`,
            {}
        ).pipe(
            tap(() => this.invalidateRunScopedCaches(runId))
        );
    }

    public getPdfStatus<T>(runId: string, refinery: string, unit: string): Observable<T> {
        const normalizedRunId = runId.trim();
        const normalizedRefinery = refinery.trim().toUpperCase();
        const normalizedUnit = unit.trim().toUpperCase();

        return this.httpService.Get<T>(
            `${HOME_API_PATHS.refineries}/${encodeURIComponent(normalizedRefinery)}/units/${encodeURIComponent(normalizedUnit)}/runs/${encodeURIComponent(normalizedRunId)}/${HOME_API_PATHS.approvals}/${HOME_API_PATHS.pdfStatus}`
        );
    }

    public getAssetMentions<T>(
        runId: string,
        refinery: string,
        unit: string,
        assetTag: string,
        page = HOME_API_DEFAULT_PAGE,
        pageSize = HOME_API_DEFAULT_PAGE_SIZE,
        source?: string
    ): Observable<T> {
        const normalizedRunId = runId.trim();
        const normalizedRefinery = refinery.trim().toUpperCase();
        const normalizedUnit = unit.trim().toUpperCase();
        const normalizedAssetTag = assetTag.trim();

        const query = new URLSearchParams({
            page: page.toString(),
            page_size: pageSize.toString()
        });

        if (source?.trim()) {
            query.set('source', source.trim());
        }

        return this.httpService.Get<T>(
            `${HOME_API_PATHS.refineries}/${encodeURIComponent(normalizedRefinery)}/units/${encodeURIComponent(normalizedUnit)}/runs/${encodeURIComponent(normalizedRunId)}/${HOME_API_PATHS.assets}/${encodeURIComponent(normalizedAssetTag)}/${HOME_API_PATHS.mentions}?${query.toString()}`,
            undefined,
            ASSET_MENTIONS_TIMEOUT_MS
        );
    }

    public getProcessSummaryPdf(runId: string, refinery: string, unit: string): Observable<Blob> {
        const normalizedRunId = runId.trim();
        const normalizedRefinery = refinery.trim().toUpperCase();
        const normalizedUnit = unit.trim().toUpperCase();

        return this.httpService.GetBlob(
            `${HOME_API_PATHS.refineries}/${encodeURIComponent(normalizedRefinery)}/units/${encodeURIComponent(normalizedUnit)}/runs/${encodeURIComponent(normalizedRunId)}/${HOME_API_PATHS.processSummaryPdf}`
        );
    }

    private getWithCacheAndBackgroundRefresh<T>(
        cache: Map<string, CacheEntry<unknown>>,
        inFlight: Map<string, Observable<unknown>>,
        key: string,
        requestFactory: () => Observable<T>
    ): Observable<T> {
        const normalizedKey = key.trim();
        const cached = cache.get(normalizedKey) as CacheEntry<T> | undefined;
        const request$ = this.getOrCreateInFlightRequest<T>(cache, inFlight, normalizedKey, requestFactory);

        if (!cached) {
            return request$;
        }

        return concat(
            of(cached.value),
            request$.pipe(
                // Keep stale-but-fast UX if refresh fails.
                catchError(() => EMPTY)
            )
        );
    }

    private getOrCreateInFlightRequest<T>(
        cache: Map<string, CacheEntry<unknown>>,
        inFlight: Map<string, Observable<unknown>>,
        key: string,
        requestFactory: () => Observable<T>
    ): Observable<T> {
        const existing = inFlight.get(key) as Observable<T> | undefined;

        if (existing) {
            return existing;
        }

        const request$ = requestFactory().pipe(
            tap((response) => {
                cache.set(key, {
                    value: response,
                    cachedAt: Date.now()
                });
            }),
            finalize(() => inFlight.delete(key)),
            shareReplay({ bufferSize: 1, refCount: false })
        );

        inFlight.set(key, request$ as Observable<unknown>);
        return request$;
    }

    private invalidateRunScopedCaches(runId: string): void {
        const normalizedRunId = runId.trim();

        this.deleteScopedRunCacheEntries(this.runSummaryCache, normalizedRunId);
        this.deleteScopedRunCacheEntries(this.runAssetsCache, normalizedRunId);
        this.deleteScopedRunCacheEntries(this.processSummaryCache, normalizedRunId);
        this.deleteScopedRunCacheEntries(this.citationsManifestCache, normalizedRunId);

        this.deleteScopedRunCacheEntries(this.runSummaryInFlight, normalizedRunId);
        this.deleteScopedRunCacheEntries(this.runAssetsInFlight, normalizedRunId);
        this.deleteScopedRunCacheEntries(this.processSummaryInFlight, normalizedRunId);
        this.deleteScopedRunCacheEntries(this.citationsManifestInFlight, normalizedRunId);
    }

    private deleteScopedRunCacheEntries<T>(cache: Map<string, T>, runId: string): void {
        const scopedRunSuffix = `|${runId}`;

        Array.from(cache.keys())
            .filter((key) => key === runId || key.endsWith(scopedRunSuffix))
            .forEach((key) => cache.delete(key));
    }
}
